import { Button, Checkbox, Tooltip } from '@assembly-js/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BatchActionsToolbarProps {
  selectionCount: number;
  showDeleteAction: boolean;
  downloadDisabled?: boolean;
  onDownload: () => void;
  onDelete: () => void;
  onClear: () => void;
  onSelectAll: () => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

const CHECKBOX_CLASSES = { root: 'bulk-ds-checkbox' };

export const BatchActionsToolbar: React.FC<BatchActionsToolbarProps> = ({
  selectionCount,
  showDeleteAction,
  downloadDisabled = false,
  onDownload,
  onDelete,
  onClear,
  onSelectAll,
  isAllSelected,
  isIndeterminate,
}) => {
  const { t } = useTranslation();

  const handleToggleAll = React.useCallback(() => {
    if (isAllSelected || isIndeterminate) {
      onClear();
    } else {
      onSelectAll();
    }
  }, [isAllSelected, isIndeterminate, onClear, onSelectAll]);

  if (selectionCount === 0) {
    return null;
  }

  const clearLabel = t(($) => $.page_files.batch_actions.clear);
  const downloadLabel = t(($) => $.page_files.batch_actions.download);
  const downloadUnavailableTooltip = t(
    ($) => $.page_files.batch_actions.download_unavailable_tooltip,
  );

  const downloadButton = (
    <Button
      variant="text"
      size="sm"
      prefixIcon="Download"
      label={downloadLabel}
      onClick={onDownload}
      disabled={downloadDisabled}
      className="!border-non-hover-border !bg-white [&>p]:hidden sm:[&>p]:block [&>svg]:!pr-0 sm:[&>svg]:!pr-2"
    />
  );

  return (
    <div className="bulk-action-bar">
      <div className="bulk-action-bar-checkbox">
        <Checkbox
          label=""
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleToggleAll}
          classes={CHECKBOX_CLASSES}
        />
      </div>
      <div className="bulk-action-bar-content">
        <div className="bulk-action-bar-left">
          <span className="bulk-action-bar-count">
            {t(($) => $.page_files.batch_actions.items_selected, {
              count: selectionCount,
            })}
          </span>
          <span className="bulk-action-bar-divider" />
          <div className="bulk-action-bar-actions">
            {downloadDisabled ? (
              <Tooltip
                content={downloadUnavailableTooltip}
                position="bottom"
                showDelayDuration={0}
              >
                {/* Disabled buttons don't receive pointer events — wrap so hover still works. */}
                <span className="inline-flex">{downloadButton}</span>
              </Tooltip>
            ) : (
              downloadButton
            )}
            {showDeleteAction && (
              <Button
                variant="text"
                size="sm"
                prefixIcon="Trash"
                label={t(($) => $.page_files.batch_actions.delete)}
                onClick={onDelete}
                className="!border-non-hover-border !bg-white !text-error-primary hover:!bg-white hover:!border-non-hover-border [&>p]:hidden sm:[&>p]:block [&>svg]:!pr-0 sm:[&>svg]:!pr-2"
              />
            )}
          </div>
        </div>
        <Button
          variant="text"
          size="sm"
          prefixIcon="Close"
          label={clearLabel}
          aria-label={clearLabel}
          onClick={onClear}
          className="!border-non-hover-border !bg-white [&>p]:hidden sm:[&>p]:block [&>svg]:!pr-0 sm:[&>svg]:!pr-2"
        />
      </div>
    </div>
  );
};
